import {
    useCallback,
    useMemo,
} from 'react'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment/moment'
import useSyncState from 'app/hooks/useSyncState'
import {
    AsyncMutationOptions,
} from 'app/types/request.types'
import {
    FilterContext, FilterContextType,
    FilteredData,
    FilterOptions,
} from 'app/hooks/useFilter'
import useLabel from 'app/hooks/useLabel'
import useEnumValues from 'app/hooks/useEnumValues'
import locationRequests from 'app/requests/Location.requests'
import {
    standardLocationConvertionFn,
    useLocationAirportLoadOptions,
} from 'app/shared-components/LocationSelector'
import useDelayedLoadOptions from 'app/hooks/useDelayedLoadOptions'
import DataContentWrapper from 'app/shared-components/DataContentWrapper'
import SkyNetSpreadSheet, {
    OnChangeCellFunction,
    SkyNetSpreadSheetControlPanel, useTypeaheadOptions,
    getEditedItems,
} from 'app/shared-components/SkyNetSpreadSheet'
import {
    AddressMainCategory,
    LaneStatus,
    PreBookingContainerStatus,
    PreBookingStatus,
} from 'app/enums/label.enums'
import strToDate from 'app/utils/date/strToDate'

import {
    LaneSelector,
} from 'app/Apps/Pricing/Pricing.types'

import useFetchLanes from './hooks/useFetchLanes'
import preBookingsFilterConfig
    from './PreBookingSpreadsheet.filterConfig'
import getConfig, {
    PreBookingEntitySpreadsheetData,
} from './PreBookingSpreadsheet.config'

const PreBookingSpreadsheet = ({
    onSave, onCancel, enableAddNewRows, filterOptions,
}: Readonly<{
    onSave?: (v: PreBookingEntitySpreadsheetData[], o?: AsyncMutationOptions) => void
    onCancel?: (...args: any[]) => void,
    enableAddNewRows?: boolean,
    filterOptions?: FilterOptions,
}>) => {
    const getLabel = useLabel()
    const getAllowedValues = useEnumValues()
    const getLanes = useFetchLanes()

    const {
        value,
        setValue,
    } = useSyncState(filterOptions?.filteredData as FilteredData<PreBookingEntitySpreadsheetData[]>)

    const filterByAccount = useMemo(() => {
        return {
            pricingAccountId: filterOptions?.filter?.pricingAccountId,
        }
    }, [filterOptions?.filter])

    const editedItems = useMemo(() => {
        return getEditedItems({
            allItems: value,
            initData: filterOptions.filteredData,
        })
    }, [
        filterOptions.filteredData,
        value,
    ])

    const handleSave = useCallback(async (options?: { onSuccess?: (...a: any) => void }) => {
        return onSave(editedItems, options)
    }, [
        editedItems,
        onSave,
    ])

    const edited = Boolean(editedItems.length)

    const saveBeforeFilter = useCallback((setFilter) => {
        handleSave({
            onSuccess: setFilter,
        })
    }, [handleSave])

    const filterConfig: FilterContextType = useMemo(() => {
        return {
            ...filterOptions,
            config: preBookingsFilterConfig,
            beforeFilterCallback: saveBeforeFilter,
            edited,
        }
    }, [
        edited,
        filterOptions,
        saveBeforeFilter,
    ])

    const loadLocationAirportOptions = useLocationAirportLoadOptions()
    const locationAirportParams = useTypeaheadOptions({
        load: loadLocationAirportOptions,
    })

    const serviceCenterOptions = useMemo(() => {
        return {
            url: locationRequests.all().url,
            includeFilters: {
                addressMainCategory: [AddressMainCategory.SERVICECENTER],
            },
            convertion: standardLocationConvertionFn,
        }
    }, [])

    const loadServiceCenterOptions = useDelayedLoadOptions(serviceCenterOptions)
    const serviceCenterParams = useTypeaheadOptions({
        load: loadServiceCenterOptions,
    })

    const applyLaneFilter = useCallback(({
        originAirport, destinationAirport,
    }: PreBookingEntitySpreadsheetData) => {
        return {
            originAirportId: [originAirport?.id],
            destinationAirportId: [destinationAirport?.id],
        }
    }, [])

    const onLaneChange: OnChangeCellFunction = useCallback(({
        handoverPointLocationName,
        collectionDropoffPointLocationName,
        originScId,
        originScLocationName,
    }: LaneSelector) => {
        return {
            handoverPointLocationName,
            collectionDropoffPointLocationName,
            originServiceCenter: originScId ? {
                id: originScId,
                value: originScId,
                label: originScLocationName,
            } : undefined,
        }
    }, [])

    const loadAvailableLanes = useCallback((options?: {
        phrase?: string,
        filters?: Record<string, any>,
    }) => {
        return getLanes({
            includeFilters: {
                laneStatus: [
                    LaneStatus.IMPLEMENTED,
                    LaneStatus.AWARDED,
                ],
                ...filterByAccount,
            },
            ...options,
        })
    }, [
        filterByAccount,
        getLanes,
    ])

    const laneParams = useTypeaheadOptions({
        load: loadAvailableLanes,
        isFilterRequired: true,
    })

    const noOriginDestinationSelected = useCallback(({
        destinationAirport, originAirport,
    }: PreBookingEntitySpreadsheetData) => {
        return isEmpty(destinationAirport) || isEmpty(originAirport)
    }, [])

    const onAirportChange: OnChangeCellFunction = useCallback(async (
        _, preBooking: PreBookingEntitySpreadsheetData,
    ) => {
        const result = {
            preBookingStatus: preBooking.preBookingStatus ?? PreBookingStatus.PENDING,
            containersStatus: preBooking.containersStatus ?? PreBookingContainerStatus.PENDING,
        }

        if (noOriginDestinationSelected(preBooking)) return result

        const lanes = await loadAvailableLanes({
            filters: {
                ...applyLaneFilter(preBooking),
            },
        })

        if (lanes?.length) {
            return {
                lane: lanes[0],
                ...result,
                ...onLaneChange(lanes[0]) as Record<string, any>,
            }
        }

        return {
            lane: undefined,
            handoverPointLocationName: undefined,
            collectionDropoffPointLocationName: undefined,
            originServiceCenter: undefined,
        }
    }, [
        applyLaneFilter,
        loadAvailableLanes,
        noOriginDestinationSelected,
        onLaneChange,
    ])

    const onPrebookingHandoverAutopopulateChange: OnChangeCellFunction = useCallback(
        (requestedHandover) => {
            if (requestedHandover) {
                return {
                    handoverTimezone: moment().format('ZZ'),
                }
            }
            return undefined
        }, [],
    )

    const onPrebookingHandoverOverrideChange: OnChangeCellFunction = useCallback(
        (requestedHandover) => {
            if (requestedHandover) {
                const date = strToDate(requestedHandover)

                return {
                    requestedHandoverYear: date.getFullYear(),
                    requestedHandoverWeek: moment(date).week(),
                }
            }
            return undefined
        }, [],
    )

    const handoverYearWeekDisabled = useCallback(({
        requestedHandover,
    }: PreBookingEntitySpreadsheetData) => {
        return !isEmpty(requestedHandover)
    }, [])

    const onPrebookingPickupAutopopulateChange: OnChangeCellFunction = useCallback(
        (requestedPickup) => {
            if (requestedPickup) {
                return {
                    pickupTimezone: moment().format('ZZ'),
                }
            }
            return undefined
        }, [],
    )

    const noChangesToRow = useCallback((prebooking: PreBookingEntitySpreadsheetData) => {
        return !(getEditedItems({
            allItems: [prebooking],
            initData: filterOptions.filteredData.filter(({
                id,
            } = {}) => {
                return id === prebooking?.id
            }),
        })?.length)
    }, [filterOptions.filteredData])

    const config = useMemo(() => {
        return getConfig({
            getLabel,
            getAllowedValues,
            editMode: !enableAddNewRows,
            locationAirportParams,
            serviceCenterParams,
            laneParams,
            noOriginDestinationSelected,
            noChangesToRow,
            applyLaneFilter,
            onLaneChange,
            onAirportChange,
            onPrebookingHandoverAutopopulateChange,
            onPrebookingHandoverOverrideChange,
            handoverYearWeekDisabled,
            onPrebookingPickupAutopopulateChange,
        })
    }, [
        getLabel,
        getAllowedValues,
        enableAddNewRows,
        locationAirportParams,
        serviceCenterParams,
        laneParams,
        noOriginDestinationSelected,
        noChangesToRow,
        applyLaneFilter,
        onLaneChange,
        onAirportChange,
        onPrebookingHandoverAutopopulateChange,
        onPrebookingHandoverOverrideChange,
        handoverYearWeekDisabled,
        onPrebookingPickupAutopopulateChange,
    ])

    return (
        <FilterContext.Provider value={filterConfig}>
            <SkyNetSpreadSheetControlPanel
                onSave={handleSave}
                onCancel={onCancel}
                edited={edited}
            >
                <DataContentWrapper
                    isEmpty={!filterByAccount.pricingAccountId}
                    emptyDataReplacer={`Please select the account to ${enableAddNewRows ? 'add new' : 'edit'} pre-bookings`}
                >
                    <SkyNetSpreadSheet
                        config={config}
                        value={value}
                        disabled={false}
                        onChange={setValue}
                    />
                </DataContentWrapper>
            </SkyNetSpreadSheetControlPanel>
        </FilterContext.Provider>
    )
}

export default PreBookingSpreadsheet
